import { createSlice } from '@reduxjs/toolkit';

const TOKEN_KEY = 'token';
const USER_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refreshToken';
const REFRESH_TOKEN_EXPIRES_AT_KEY = 'refreshTokenExpiresAt';
const REFRESH_TOKEN_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const removeStoredRefreshToken = () => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_EXPIRES_AT_KEY);
};

export const getStoredUser = () => {
  const savedUser = localStorage.getItem(USER_KEY);

  return savedUser ? JSON.parse(savedUser) : null;
};

export const persistUser = (user) => {
  if (user === null) {
    localStorage.removeItem(USER_KEY);
    return;
  }

  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

export const setStoredAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

export const persistRefreshToken = (refreshToken) => {
  if (!refreshToken) {
    removeStoredRefreshToken();
    return;
  }

  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(
    REFRESH_TOKEN_EXPIRES_AT_KEY,
    String(Date.now() + REFRESH_TOKEN_TTL_MS)
  );
};

export const getStoredRefreshToken = () => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const expiresAt = Number(localStorage.getItem(REFRESH_TOKEN_EXPIRES_AT_KEY));

  if (!refreshToken || !expiresAt || Number.isNaN(expiresAt)) {
    removeStoredRefreshToken();
    return null;
  }

  if (Date.now() >= expiresAt) {
    removeStoredRefreshToken();
    return null;
  }

  return refreshToken;
};

export const clearStoredAuthData = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  removeStoredRefreshToken();
};

const initState = {
  token: localStorage.getItem(TOKEN_KEY),
  user: getStoredUser(),
};

getStoredRefreshToken();

const authSlice = createSlice({
  name: 'auth',
  initialState: initState,
  reducers: {
    saveAuthToken: (state, action) => {
      const { user, token, refreshToken } = action.payload;
      state.token = token;
      state.user = user;

      setStoredAuthToken(token);
      persistUser(user);

      if (refreshToken !== undefined) {
        persistRefreshToken(refreshToken);
      }
    },
    updateAuthToken: (state, action) => {
      state.token = action.payload;
      setStoredAuthToken(action.payload);
    },
    updateRefreshToken: (state, action) => {
      persistRefreshToken(action.payload);
    },
    updateTargetCanteen: (state, action) => {
      if (!state.user) {
        return;
      }

      const updatedUser = {
        ...state.user,
        canteen: action.payload,
      };

      state.user = updatedUser;
      persistUser(updatedUser);
    },
    clearTargetCanteen: (state) => {
      if (!state.user) {
        return;
      }

      const updatedUser = {
        ...state.user,
        canteen: null,
      };

      state.user = updatedUser;
      persistUser(updatedUser);
    },
    logout: (state) => {
      clearStoredAuthData();
      state.token = null;
      state.user = null;
    },
  },
});

export const AuthActions = authSlice.actions;
export default authSlice;
